import { useEffect, useMemo, useState } from 'react';

import { IListUsersParams, listAllUsers } from '../services/identity/listAllUsers';
import { UserProfile } from '../services/identity/types/UserProfile';

interface IUseUsers {
  users: UserProfile[];
  dataLoading: boolean;
  totalRecords: number;
  currentPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
}

export default function useUsers(
  params: Pick<
    IListUsersParams,
    | 'sort_by'
    | 'sort_direction'
    | 'full_name'
    | 'email'
    | 'status'
    | 'type'
    | 'limit'
    | 'organization_ids'
    | 'client_tags'
    | 'page'
    | 'skip'
  >,
): IUseUsers {
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [dataLoading, setDataLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState(1);

  // Stable serialized key so the effect only re-runs when params actually change
  const paramsKey = useMemo(() => JSON.stringify(params), [params]);

  useEffect(() => {
    const abortController = new AbortController();
    setDataLoading(true);

    listAllUsers(params, abortController.signal)
      .then((usersList) => {
        if (!abortController.signal.aborted) {
          setTotalRecords(usersList.total);
          setUsers([...usersList.nodes]);
          setDataLoading(false);
        }
      })
      .catch((error) => {
        if (!abortController.signal.aborted) {
          console.error('Failed to fetch users:', error);
          setDataLoading(false);
        }
      });

    return (): void => {
      abortController.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => {
    setCurrentPage(1);
  }, [params.full_name, params.email, params.status, params.type]);

  return {
    users,
    totalRecords,
    dataLoading,
    currentPage,
    setCurrentPage,
  };
}
